import React, { useEffect, useMemo, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFloppyDisk, faSpinner, faTrash } from '@fortawesome/free-solid-svg-icons'
import { useControlCenterFilterPreset } from '../../hooks/useControlCenterFilterPreset'
import {
  DEFAULT_FILTER_PRESET,
  normalizeFilterPreset,
  type ControlCenterFilterPreset,
} from '../../utils/controlCenterFilterPreset'
import { API_BASE_URL } from '../../utils/apiBaseUrl'
import { apiFetch } from '../../utils/apiFetch'
import { mapJobtreadJobRow } from '../../utils/controlCenterJobtreadJobs'
import { mapMunicipalityLicenseRow, STATE_ABBR_TO_FIPS } from '../../utils/controlCenterMunicipalityLicenses'
import {
  fleetEntityToControlCenterAsset,
} from '../../utils/controlCenterLinxupAssets'
import { normalizeFleetEntities } from '../../utils/tallyFleetEntities'
import { OMNI_CATEGORY_ORDER, OMNI_GROUP_LABEL } from './map/omniSearch'
import { OmniCategoryFilterFields } from './map/OmniCategoryFilterFields'
import type { ControlCenterJob } from '../../types/controlCenter'

const US_STATE_ABBRS = Object.keys(STATE_ABBR_TO_FIPS).sort()

/**
 * Control Center Management → Filter Presets.
 * Persists to backend/data/controlCenterFilterPreset.json (not Mongo).
 */
export default function FilterPresetManager() {
  const { config, loading, error, save, refresh } = useControlCenterFilterPreset()
  const [draft, setDraft] = useState<ControlCenterFilterPreset>(() => normalizeFilterPreset(config))
  const [saving, setSaving] = useState(false)
  const [saveMessage, setSaveMessage] = useState<string | null>(null)
  const [jobProjectTypeOptions, setJobProjectTypeOptions] = useState<string[]>([])
  const [jobStatusOptions, setJobStatusOptions] = useState<string[]>([])
  const [jobStatesFromData, setJobStatesFromData] = useState<string[]>([])
  const [assetStatusOptions, setAssetStatusOptions] = useState<string[]>([])
  const [municipalityStatesFromData, setMunicipalityStatesFromData] = useState<string[]>([])

  useEffect(() => {
    setDraft(normalizeFilterPreset(config))
  }, [config])

  useEffect(() => {
    let cancelled = false
    const token = localStorage.getItem('lts_jwt_token')
    const authHeaders: Record<string, string> = {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    }

    const uniqueSorted = (values: Array<string | null | undefined>) =>
      [...new Set(values.map((v) => String(v || '').trim()).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b),
      )

    ;(async () => {
      try {
        const res = await apiFetch(`${API_BASE_URL}/capabilities/execute`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders },
          body: JSON.stringify({
            app: 'controlCenter',
            capability: 'jobtread.listJobsWithLocations',
            input: {},
          }),
        })
        const payload = await res.json().catch(() => ({}))
        const rows: Record<string, unknown>[] = Array.isArray(payload?.data?.jobs)
          ? payload.data.jobs
          : []
        const jobs = rows
          .map((row) => mapJobtreadJobRow(row))
          .filter((job): job is ControlCenterJob => job != null)
        if (!cancelled) {
          setJobProjectTypeOptions(uniqueSorted(jobs.map((j) => j.projectType)))
          setJobStatusOptions(uniqueSorted(jobs.map((j) => j.status)))
          setJobStatesFromData(uniqueSorted(jobs.map((j) => j.state)))
        }
      } catch {
        /* keep empty option lists */
      }
    })()

    ;(async () => {
      try {
        const res = await apiFetch(`${API_BASE_URL}/integrations/linxup/locations`, {
          headers: authHeaders,
        })
        const payload = await res.json().catch(() => ({}))
        const entities = normalizeFleetEntities(payload.data)
        const assets = entities.map(fleetEntityToControlCenterAsset)
        if (!cancelled) {
          setAssetStatusOptions(uniqueSorted(assets.map((a) => a.status)))
        }
      } catch {
        /* keep empty option lists */
      }
    })()

    ;(async () => {
      try {
        const res = await apiFetch(`${API_BASE_URL}/municipality-licenses`, {
          headers: authHeaders,
        })
        const payload = await res.json().catch(() => ({}))
        const rows: Record<string, unknown>[] = Array.isArray(payload.data) ? payload.data : []
        const licenses = rows.map((row) => mapMunicipalityLicenseRow(row))
        if (!cancelled) {
          setMunicipalityStatesFromData(uniqueSorted(licenses.map((m) => m.state)))
        }
      } catch {
        /* keep empty option lists */
      }
    })()

    return () => {
      cancelled = true
    }
  }, [])

  const jobStates = useMemo(
    () => uniqueMerge(US_STATE_ABBRS, jobStatesFromData),
    [jobStatesFromData],
  )
  const municipalityStates = useMemo(
    () => uniqueMerge(US_STATE_ABBRS, municipalityStatesFromData),
    [municipalityStatesFromData],
  )

  const patchDraft = (partial: Partial<ControlCenterFilterPreset>) => {
    setDraft((prev) => ({ ...prev, ...partial }))
    setSaveMessage(null)
  }

  const filterFieldsProps = {
    jobFilters: draft.jobFilters,
    personFilters: draft.personFilters,
    assetFilters: draft.assetFilters,
    municipalityFilters: draft.municipalityFilters,
    onJobFiltersChange: (next: ControlCenterFilterPreset['jobFilters']) =>
      patchDraft({ jobFilters: next }),
    onPersonFiltersChange: (next: ControlCenterFilterPreset['personFilters']) =>
      patchDraft({ personFilters: next }),
    onAssetFiltersChange: (next: ControlCenterFilterPreset['assetFilters']) =>
      patchDraft({ assetFilters: next }),
    onMunicipalityFiltersChange: (next: ControlCenterFilterPreset['municipalityFilters']) =>
      patchDraft({ municipalityFilters: next }),
    jobStates,
    jobProjectTypeOptions,
    jobStatusOptions,
    assetStatusOptions,
    municipalityStates,
    showAllJobs: draft.showAllJobs,
    showAllPeople: draft.showAllPeople,
    showAllAssets: draft.showAllAssets,
    onShowAllJobsChange: (next: boolean) => patchDraft({ showAllJobs: next }),
    onShowAllPeopleChange: (next: boolean) => patchDraft({ showAllPeople: next }),
    onShowAllAssetsChange: (next: boolean) => patchDraft({ showAllAssets: next }),
    boundaryShowLevel: draft.boundaryShowLevel,
    onBoundaryShowLevelChange: (next: ControlCenterFilterPreset['boundaryShowLevel']) =>
      patchDraft({ boundaryShowLevel: next }),
  }

  const onSave = async () => {
    setSaving(true)
    setSaveMessage(null)
    try {
      await save(draft)
      setSaveMessage('Saved.')
    } catch (err) {
      setSaveMessage(err instanceof Error ? err.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const onReset = () => {
    setDraft(normalizeFilterPreset(DEFAULT_FILTER_PRESET))
    setSaveMessage(null)
  }

  if (loading && !config) {
    return (
      <div className="flex items-center gap-2 p-4 text-sm text-base-content/70">
        <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
        Loading filter presets…
      </div>
    )
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
      <div className="mx-auto w-full max-w-2xl space-y-6 p-4">
        {error ? (
          <div className="alert alert-warning text-sm">
            <span>{error}</span>
            <button type="button" className="btn btn-ghost btn-xs" onClick={() => void refresh()}>
              Retry
            </button>
          </div>
        ) : null}

        <div>
          <h3 className="text-lg font-semibold">Default filters</h3>
          <p className="mt-1 text-sm text-base-content/60">
            Applied when someone first opens Control Center. People can still change filters during
            their session. Stored in a server config file (same pattern as Operation Config), not a
            database collection.
          </p>
        </div>

        {OMNI_CATEGORY_ORDER.map((category) => (
          <section key={category} className="rounded-lg border border-base-300 bg-base-100 p-4">
            <h3 className="text-sm font-semibold">{OMNI_GROUP_LABEL[category]}</h3>
            <p className="mt-0.5 mb-3 text-xs text-base-content/55">
              {category === 'municipality'
                ? 'Show-all draws boundary areas. The filters below apply to municipality search and overlays.'
                : `Show-all plots every matching ${OMNI_GROUP_LABEL[category].toLowerCase()} on the map. Filters limit that set and omni search.`}
            </p>
            <OmniCategoryFilterFields category={category} {...filterFieldsProps} />
          </section>
        ))}

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="btn btn-primary btn-sm gap-2"
            disabled={saving}
            onClick={() => void onSave()}
          >
            <FontAwesomeIcon
              icon={saving ? faSpinner : faFloppyDisk}
              className={saving ? 'animate-spin' : ''}
            />
            Save
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-sm gap-2"
            disabled={saving}
            onClick={onReset}
          >
            <FontAwesomeIcon icon={faTrash} />
            Reset to none
          </button>
          {saveMessage ? (
            <span className="text-sm text-base-content/60">{saveMessage}</span>
          ) : null}
        </div>
      </div>
    </div>
  )
}

function uniqueMerge(...lists: string[][]): string[] {
  return [...new Set(lists.flat().filter(Boolean))].sort((a, b) => a.localeCompare(b))
}
